import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import * as v from 'valibot';
import {
  PrivateAIAssistantChatThreadSchema,
  RTDBPath,
  type PrivateAIAssistantChatThreadJSON,
  type ViewModelId,
} from 'balus-common';
import { useSnapshot } from '@framework/hooks/useSnapshot';
import { useRequiredCurrentUserId } from '@framework/auth';
import { Timestamp } from '@framework/Timestamp';
import { formatRelativeTime, type RelativeTimeI18nKeys } from '@framework/i18n/formatRelativeTime';
import { useNow } from '@framework/hooks/useNow';
import { useAIAssistantRouter, useRequestAIAssistantThread } from './aiAssistantRouteAtom';
import { useStartNewAIAssistantChat } from './useStartNewAIAssistantChat';

const RELATIVE_TIME_I18N_KEYS = {
  minutes: 'viewModel.aiChat.relativeTime.minutes',
  hours: 'viewModel.aiChat.relativeTime.hours',
  days: 'viewModel.aiChat.relativeTime.days',
  weeks: 'viewModel.aiChat.relativeTime.weeks',
  months: 'viewModel.aiChat.relativeTime.months',
  years: 'viewModel.aiChat.relativeTime.years',
} as const satisfies RelativeTimeI18nKeys;

export function AIAssistantChatList({ viewModelId }: { viewModelId: ViewModelId }) {
  const { t } = useTranslation();
  const userId = useRequiredCurrentUserId();
  const router = useAIAssistantRouter(viewModelId);
  const requestThread = useRequestAIAssistantThread(viewModelId);
  const startNewChat = useStartNewAIAssistantChat(viewModelId);
  const now = useNow();
  const loadFailedMessage = t('viewModel.aiChat.toastLoadHistoryError');

  const [threads, { loading }] = useSnapshot({
    path: RTDBPath.ViewModel.privateAIAssistantChatThreadsPath(viewModelId, userId),
    load: ({ getChildValues }) => {
      const result: PrivateAIAssistantChatThreadJSON[] = [];
      for (const [threadId, raw] of Object.entries(getChildValues())) {
        const parsed = v.safeParse(PrivateAIAssistantChatThreadSchema, raw);
        if (!parsed.success || parsed.output.id !== threadId) continue;
        // メッセージが一度も保存されていない空スレッド（旧実装が作成したもの）は一覧に出さない。
        // メッセージ保存時は touch で updatedAt が進むため createdAt と一致しなくなる
        if (parsed.output.title === '' && parsed.output.updatedAt === parsed.output.createdAt) continue;
        result.push(parsed.output);
      }
      // 更新が新しい順。同時刻の場合は push キーの時系列性を利用して新しい方を先にする
      result.sort((a, b) => b.updatedAt - a.updatedAt || b.id.localeCompare(a.id));
      return result;
    },
    onError: (error) => {
      console.error('Failed to load AI assistant chat threads', error);
      toast.error(loadFailedMessage);
    },
  });

  const openThread = (threadId: string) => {
    requestThread(threadId);
    router.push({ page: 'chat' });
  };

  return (
    <div className="flex min-h-0 grow flex-col">
      {loading ? (
        <div className="flex grow items-center justify-center py-8 text-sm text-gray-500">
          {t('viewModel.aiChat.loadingHistory')}
        </div>
      ) : threads === null || threads.length === 0 ? (
        <div className="flex flex-col gap-4 rounded bg-gray-50 px-6 py-8">
          <h3 className="text-center text-base font-bold text-gray-900">{t('viewModel.aiChat.emptyStateTitle')}</h3>
          <p className="text-sm leading-relaxed text-gray-700">{t('viewModel.aiChat.emptyStateDescription')}</p>
          <div className="flex justify-center">
            <button
              className="rounded-md bg-teal-600 px-4 py-2 text-sm font-bold text-white hover:bg-teal-700"
              onClick={startNewChat}
            >
              {t('viewModel.aiChat.startChatLabel')}
            </button>
          </div>
        </div>
      ) : (
        <ul className="-mx-2 grow overflow-y-auto">
          {threads.map((thread) => (
            <li key={thread.id}>
              <button
                type="button"
                className="flex w-full flex-col gap-0.5 rounded p-2 text-left hover:bg-gray-100"
                onClick={() => openThread(thread.id)}
              >
                <span className="w-full truncate text-sm text-gray-800">
                  {thread.title !== '' ? thread.title : t('viewModel.aiChat.historyUntitled')}
                </span>
                <span className="text-xs text-gray-500">
                  {formatRelativeTime(Timestamp.fromUnixTimestamp(thread.updatedAt), now, t, RELATIVE_TIME_I18N_KEYS)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
